import type { FunctionComponent } from 'react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import type { TemplatesTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import type { TemplatesStoryQuery } from './TemplatesStoryQuery.generated';
import { TemplatesTile } from './TemplatesTile';

import styles from './RelatedTemplates.less';

type RelatedTemplates = NonNullable<TemplatesStoryQuery['board']>['related'];

interface RelatedTemplatesProps {
  templates: RelatedTemplates;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const RelatedTemplates: FunctionComponent<RelatedTemplatesProps> = ({
  templates,
}) => {
  const tiles = templates.map((t) => {
    return (
      <TemplatesTile
        key={t.id}
        avatarUrl={t.templateGallery?.avatarUrl || ''}
        avatarShape={t.templateGallery?.avatarShape || ''}
        backgroundColor={t.prefs?.backgroundColor || ''}
        backgroundUrl={t.prefs?.backgroundImage || ''}
        blurb={t.templateGallery?.blurb || ''}
        boardId={t.id}
        byline={t.templateGallery?.byline || ''}
        category={t.templateGallery?.category || ''}
        idOrganization={t.idOrganization || ''}
        shortLink={t.shortLink}
        title={t.name}
        viewCount={t.templateGallery?.stats.viewCount || 0}
        copyCount={t.templateGallery?.stats.copyCount || 0}
        analyticsSource={'relatedTemplatesStoryPageSection'}
      />
    );
  });

  useEffect(() => {
    Analytics.sendViewedComponentEvent({
      componentType: 'tile',
      componentName: 'templateTile',
      source: 'relatedTemplatesStoryPageSection',
    });
  }, []);

  return (
    <div
      data-testid={getTestId<TemplatesTestIds>('related-templates-container')}
      className={styles.relatedTemplatesContainer}
    >
      <FormattedMessage
        id="templates.templates.related-templates"
        defaultMessage="Related templates"
        description="A display of related templates"
        tagName="h2"
      />
      <div className={styles.tilesContainer}>{tiles}</div>
    </div>
  );
};
